import { useEffect, useRef, useState } from 'react';
import type { Project } from '@canvas/shared-types';
import { useProjectStore } from '@/stores/projectStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { useLayoutMode } from '@/hooks/useLayoutMode';
import { registerEditorCommand } from '@/lib/editorCommands';
import { requestEditorSave } from '@/lib/editorSave';
import { ensureMovieTimeline, syncProjectIntoStores } from '@/lib/editorSession';
import CanvasEditorWithAutoSave from './CanvasEditorWithAutoSave';
import EditorMenuBar from './EditorMenuBar';
import ArtboardSizeDialog from './ArtboardSizeDialog';
import '../styles/editor-adobe.css';
import '../styles/video-editor.css';

interface EditorViewProps {
  project: Project;
  onBack: () => void;
  /** Design keeps the page strip. Movie Studio keeps the timeline on. */
  studio?: 'design' | 'movie';
  /** Design editor: copy this project into a new movie and open it. */
  onMakeMovie?: () => void;
}

export default function EditorView({
  project,
  onBack,
  studio = 'design',
  onMakeMovie,
}: EditorViewProps) {
  const currentProject = useProjectStore((s) => s.currentProject);
  const isDirty = useCanvasStore((s) => s.isDirty);
  const layoutMode = useLayoutMode();
  const [zoom, setZoom] = useState(100);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const syncedProjectIdRef = useRef<string | null>(null);

  if (syncedProjectIdRef.current !== project.projectId) {
    syncedProjectIdRef.current = project.projectId;
    syncProjectIntoStores(project);
    if (studio === 'movie') ensureMovieTimeline();
  }

  const doc = currentProject || project;

  useEffect(() => {
    registerEditorCommand('artboard-size', () => setSizeOpen(true));
    return () => registerEditorCommand('artboard-size', null);
  }, []);

  const handleBack = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await requestEditorSave();
    } finally {
      onBack();
      setLeaving(false);
    }
  };

  return (
    <div
      className={`ps-app ps-layout-${layoutMode}`}
      data-layout={layoutMode}
    >
      <EditorMenuBar
        projectName={doc.name}
        isDirty={isDirty}
        artboardLabel={`${doc.width} × ${doc.height}px`}
        leaving={leaving}
        compact={layoutMode === 'tablet' || layoutMode === 'phone'}
        phone={layoutMode === 'phone'}
        studio={studio}
        onMakeMovie={onMakeMovie}
        onClose={() => void handleBack()}
        onArtboardSize={() => setSizeOpen(true)}
      />

      <div className="editor-body" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <CanvasEditorWithAutoSave
          key={project.projectId}
          projectId={project.projectId}
          studio={studio}
          zoom={zoom}
          onZoomChange={setZoom}
        />
      </div>

      <ArtboardSizeDialog
        open={sizeOpen}
        width={doc.width}
        height={doc.height}
        onClose={() => setSizeOpen(false)}
      />
    </div>
  );
}
